// Chemin: C:\gestion-copro\src\pages\coproprietaire\DocumentsPublicsPage.tsx
// Accès aux documents partagés par le CS
// Session 56 : création initiale

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Download,
  Eye,
  Search,
  Filter,
  Loader2,
  Calendar,
  FolderOpen,
  File,
  FileImage,
  FileSpreadsheet,
  Presentation,
  ArrowLeft,
  ExternalLink,
  Info
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface Document {
  id: string;
  titre: string;
  description?: string;
  type: string;
  fichierUrl: string;
  fichierNom: string;
  fichierTaille: number;
  fichierType: string;
  dateDocument: Timestamp;
  createdAt: Timestamp;
  categorieAcces?: string;
  estPublic?: boolean;
}

// Types de documents
const TYPES_DOCUMENTS: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  pv_ag: { label: 'PV d\'Assemblée Générale', icon: FileText, color: 'text-blue-600 bg-blue-100' },
  pv_reunion: { label: 'PV de réunion CS', icon: FileText, color: 'text-indigo-600 bg-indigo-100' },
  reglement: { label: 'Règlement de copropriété', icon: File, color: 'text-purple-600 bg-purple-100' },
  budget: { label: 'Budget / Comptes', icon: FileSpreadsheet, color: 'text-green-600 bg-green-100' },
  contrat: { label: 'Contrat', icon: FileText, color: 'text-orange-600 bg-orange-100' },
  notice: { label: 'Notice / Guide', icon: FileText, color: 'text-cyan-600 bg-cyan-100' },
  rapport: { label: 'Rapport d\'expert', icon: FileText, color: 'text-red-600 bg-red-100' },
  communication: { label: 'Communication CS', icon: FileText, color: 'text-emerald-600 bg-emerald-100' },
  photo: { label: 'Photo', icon: FileImage, color: 'text-pink-600 bg-pink-100' },
  autre: { label: 'Autre document', icon: File, color: 'text-gray-600 bg-gray-100' }
};

// Catégories pour le filtre
const CATEGORIES_FILTRE = [
  { value: 'tous', label: 'Tous les documents' },
  { value: 'pv_ag', label: 'PV d\'AG' },
  { value: 'pv_reunion', label: 'PV de réunion' },
  { value: 'reglement', label: 'Règlement' },
  { value: 'budget', label: 'Budget / Comptes' },
  { value: 'contrat', label: 'Contrats' },
  { value: 'communication', label: 'Communications' },
  { value: 'autre', label: 'Autres' }
];

const DocumentsPublicsPage: React.FC = () => {
  const { firebaseUser } = useAuth();
  const { copropriete } = useCopropriete();

  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtreType, setFiltreType] = useState<string>('tous');
  const [recherche, setRecherche] = useState('');

  // Charger les documents publics
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!copropriete?.id) {
        setLoading(false);
        return;
      }

      try {
        const documentsRef = collection(db, 'documents');
        // Récupérer les documents publics de la copropriété
        const q = query(
          documentsRef,
          where('coproprieteId', '==', copropriete.id),
          where('estPublic', '==', true),
          orderBy('dateDocument', 'desc')
        );

        const snapshot = await getDocs(q);
        const data: Document[] = [];

        snapshot.forEach((doc) => {
          data.push({
            id: doc.id,
            ...doc.data()
          } as Document);
        });

        setDocuments(data);
      } catch (error) {
        console.error('Erreur chargement documents:', error);
        // Si l'index n'existe pas, essayer sans le filtre estPublic
        try {
          const documentsRef = collection(db, 'documents');
          const q = query(
            documentsRef,
            where('coproprieteId', '==', copropriete.id),
            orderBy('dateDocument', 'desc')
          );
          const snapshot = await getDocs(q);
          const data: Document[] = [];
          snapshot.forEach((doc) => {
            const docData = doc.data();
            // Filtrer côté client si estPublic n'est pas indexé
            if (docData.estPublic === true || docData.categorieAcces === 'public') {
              data.push({
                id: doc.id,
                ...docData
              } as Document);
            }
          });
          setDocuments(data);
        } catch (fallbackError) {
          console.error('Erreur fallback:', fallbackError);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [copropriete?.id]);

  // Filtrage
  const documentsFiltres = documents.filter(doc => {
    // Filtre par type
    if (filtreType !== 'tous' && doc.type !== filtreType) {
      return false;
    }
    // Filtre par recherche
    if (recherche) {
      const searchLower = recherche.toLowerCase();
      return (
        doc.titre.toLowerCase().includes(searchLower) ||
        (doc.description && doc.description.toLowerCase().includes(searchLower))
      );
    }
    return true;
  });

  // Grouper par année
  const documentsParAnnee = documentsFiltres.reduce((acc, doc) => {
    const year = doc.dateDocument?.toDate().getFullYear() || 'Non daté';
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(doc);
    return acc;
  }, {} as Record<string | number, Document[]>);

  // Trier les années (plus récente en premier)
  const anneesTriees = Object.keys(documentsParAnnee).sort((a, b) => {
    if (a === 'Non daté') return 1;
    if (b === 'Non daté') return -1;
    return Number(b) - Number(a);
  });

  // Formater la date
  const formatDate = (timestamp: Timestamp) => {
    if (!timestamp) return 'Non daté';
    const date = timestamp.toDate();
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Formater la taille du fichier
  const formatFileSize = (bytes: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  // Obtenir l'icône et la couleur selon le type
  const getTypeConfig = (type: string) => {
    return TYPES_DOCUMENTS[type] || TYPES_DOCUMENTS.autre;
  };

  // Ouvrir le document
  const handleOpenDocument = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Télécharger le document
  const handleDownload = async (doc: Document) => {
    try {
      const response = await fetch(doc.fichierUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fichierNom || `${doc.titre}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      // Fallback : ouvrir dans un nouvel onglet
      window.open(doc.fichierUrl, '_blank');
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
        <p className="text-gray-600 mt-1">
          Accédez aux documents partagés par le Conseil Syndical
        </p>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p>
              Cette section contient les documents officiels de la copropriété accessibles à tous les copropriétaires : 
              PV d'assemblées générales, règlement de copropriété, budgets, communications du CS, etc.
            </p>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Recherche */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Rechercher un document..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          {/* Filtre type */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filtreType}
              onChange={(e) => setFiltreType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              {CATEGORIES_FILTRE.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Liste des documents */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : documentsFiltres.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FolderOpen className="w-8 h-8 text-gray-400" />
          </div>
          {documents.length === 0 ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun document disponible
              </h3>
              <p className="text-gray-500">
                Le Conseil Syndical n'a pas encore partagé de documents
              </p>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun résultat
              </h3>
              <p className="text-gray-500">
                Aucun document ne correspond à vos critères de recherche
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {anneesTriees.map((annee) => (
            <div key={annee}>
              {/* En-tête année */}
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-lg font-semibold text-gray-900">{annee}</h2>
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-sm text-gray-500">
                  {documentsParAnnee[annee].length} document{documentsParAnnee[annee].length > 1 ? 's' : ''}
                </span>
              </div>

              {/* Documents de l'année */}
              <div className="space-y-3">
                {documentsParAnnee[annee].map((doc) => {
                  const typeConfig = getTypeConfig(doc.type);
                  const Icon = typeConfig.icon;

                  return (
                    <div
                      key={doc.id}
                      className="bg-white rounded-xl border border-gray-200 p-4 hover:border-green-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-start gap-4">
                        {/* Icône type */}
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${typeConfig.color}`}>
                          <Icon className="w-6 h-6" />
                        </div>

                        {/* Contenu */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900">
                            {doc.titre}
                          </h3>
                          {doc.description && (
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                              {doc.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {formatDate(doc.dateDocument)}
                            </span>
                            <span>{TYPES_DOCUMENTS[doc.type]?.label || 'Document'}</span>
                            {doc.fichierTaille > 0 && (
                              <span>{formatFileSize(doc.fichierTaille)}</span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleOpenDocument(doc.fichierUrl)}
                            className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Visualiser"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDownload(doc)}
                            className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Télécharger"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats en bas */}
      {documents.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4 text-center">
          <p className="text-sm text-gray-600">
            {documents.length} document{documents.length > 1 ? 's' : ''} disponible{documents.length > 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default DocumentsPublicsPage;
